import { useEffect, useState, useCallback, useRef } from "react";
import { useAppSelector } from "../../../hooks/redux";
import { useModalManager } from "../../../hooks/useModalManager";
import { useUser } from "../../../hooks/useUser";
import { useUserPermissions } from "../../../hooks/useUserPermissions";
import { adminApi } from "../../../repositories/admin";
import { pitchesApi } from "../../../repositories/pitches";
import { Menu } from "@headlessui/react";

import {
  EllipsisVerticalIcon,
  UserPlusIcon,
  LightBulbIcon,
  ArrowUpIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronDownIcon,
  XMarkIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import DashboardTableHeader from "./DashboardTableHeader";
import DashboardTableRow from "./DashboardTableRow";
import Pagination from "../../../components/common/Pagination";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { EyeIcon } from "lucide-react";
import { userApi } from "../../../repositories/user";
import { transactionApi } from "../../../repositories/transaction";
import { handleError } from "../../../utils/errorHandler";
import CSVUpload from "./CSVUpload";
import ModalRenderer from "../../../components/modal/ModalRenderer";
import { useAppDispatch } from "../../../hooks/redux";
import { setAuth } from "../../../store/slices/authSlice";
import PageHeader from "../../../components/ui/PageHeader";
import StatCard from "../../../components/ui/StatCard";
import DataTable, { DataTableColumn } from "../../../components/ui/DataTable";
import Avatar from "../../../components/ui/Avatar";
import SegmentedTabs from "../../../components/ui/SegmentedTabs";
import FilterPills from "../../../components/ui/FilterPills";
import { DocumentTextIcon, UsersIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import {
  rowMenuPanelClass,
  rowMenuTransitionClass,
  rowMenuTriggerClass,
} from "../../../components/ui/RowActionsMenu";
import { cn } from "../../lib/utils";
import Button from "../../../components/ui/Button";
import { confirmDanger } from "../../../utils/swal";


interface PurchaseData {
  id: number;
  email: string;
  productId: string;
  status: string;
  transactionId: string;
  pitchLimit: number;
  userId: number;
  packageName: string;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

interface Price {
  id: string;
  object: string;
  active: boolean;
  billing_scheme: string;
  created: number;
  currency: string;
  custom_unit_amount: number | null;
  livemode: boolean;
  lookup_key: string | null;
  metadata: { [key: string]: string };
  nickname: string | null;
  product: string;
  recurring: any | null;
  tax_behavior: string;
  tiers_mode: string | null;
  transform_quantity: any | null;
  type: string;
  unit_amount: number;
  unit_amount_decimal: string;
}

interface StripeProduct {
  id: string;
  name: string;
  description: string;
  active: boolean;
  metadata: {
    payment_link: string;
    pitch_limit: string;
    product_id: string;
  };
  prices: Price[];
}

interface ProfileData {
  purchase: PurchaseData | null;
}
interface Pitch {
  id: number;
  userId: number;
  companyName: string;
  artistName: string;
  releaseTitle: string;
  releaseDate: string;
  configuration: string;
  releaseUPC: string;
  trackISRC: string;
  status: string;
  deliveredDSPs?: string[];
  AmazonInfo?: any;
  SpotifyInfo?: any;
  DeezerInfo?: any;
  TidalInfo?: any;
  AudiomackInfo?: any;
}

export default function DashboardTable() {
  const dispatch = useAppDispatch();
  const [pitchesLoading, setPitchesLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const { user } = useAppSelector((state) => state.auth);
  const { fetchProfile } = useUser();
  const userType = useAppSelector((state) => state.user.userType);
  const userFullName = useAppSelector((state) => state.user.fullName);
  const { pitches } = useUserPermissions();
  const navigate = useNavigate();
  const { openModal, closeModal, isOpen } = useModalManager();

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPitches, setTotalPitches] = useState(0);
  const [usersCurrentPage, setUsersCurrentPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [profileData, setProfileData] = useState<ProfileData>({
    purchase: null,
  });
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [selectedPitchIds, setSelectedPitchIds] = useState<number[]>([]);
  // Monotonic id for pitch requests, so a superseded response cannot overwrite
  // a newer one when filters are toggled quickly.
  const pitchRequestRef = useRef(0);

  // Search and filter states for pitches
  const [pitchSearchTerm, setPitchSearchTerm] = useState("");
  const [pitchSearchType, setPitchSearchType] = useState<"artist" | "release" | "company">("artist");
  const [pitchStatusFilter, setPitchStatusFilter] = useState<string[]>([]);
  // Admin-only: which section tab is showing. Both tables still fetch on load,
  // so switching tabs never refetches and each keeps its own pagination state.
  const [activeSection, setActiveSection] = useState<"pitches" | "users">("pitches");

  // Search and filter states for users
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [userSearchType, setUserSearchType] = useState<"name" | "email" | "company">("name");
  // CSV upload
  const [showCSVUpload, setShowCSVUpload] = useState(false);

  // Modal-specific state
  const [currentDSPPitchId, setCurrentDSPPitchId] = useState<number | undefined>();
  const [currentDSPMode, setCurrentDSPMode] = useState<"admin" | "user">("admin");
  const [currentDSPs, setCurrentDSPs] = useState<string[]>([]);

  // Set Reminder modal state
  const [reminderPitchId, setReminderPitchId] = useState<number | undefined>();
  const [reminderArtistName, setReminderArtistName] = useState<string | undefined>();
  const [reminderReleaseTitle, setReminderReleaseTitle] = useState<string | undefined>();

  const [currentDeletePitchId, setCurrentDeletePitchId] = useState<number | undefined>();
  const [deleteInput, setDeleteInput] = useState("");
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const userData = await fetchProfile();
        if (userData) {
          setProfileData({
            purchase: userData.purchase || null,
          });
        }

        const productsResponse = await transactionApi.getAllStripeProducts();
        if (!productsResponse?.data?.success) {
          throw new Error(
            productsResponse?.data?.error || "Failed to fetch products"
          );
        }
        setProducts(productsResponse.data.data || []);

        // Automatically open app updates modal for basic users
        if (user?.role === "basic") {
          openModal("USER_APP_UPDATES");
        }
      } catch (error) {
        handleError(error);
      }
    };

    fetchData();
    fetchInitialData();
  }, [fetchProfile, user?.role, openModal]);

  // Modal handlers
  const handleOpenDSPModal = (pitchId: number, mode: "admin" | "user", deliveredDSPs?: string[]) => {
    setCurrentDSPPitchId(pitchId);
    setCurrentDSPMode(mode);
    setCurrentDSPs(deliveredDSPs || []);
    openModal("DSP_DELIVERY");
  };

  const handleImpersonateUser = async (userId: number) => {
    try {
      const response = await adminApi.impersonateUser(userId);
      if (response?.data?.success) {
        const { token: impersonatedToken, user: impersonatedUser } = response.data.data;

        // Save current admin auth so we can restore it later
        const currentAuth = localStorage.getItem("auth");
        if (currentAuth) {
          localStorage.setItem("adminAuth", currentAuth);
        }

        dispatch(
          setAuth({
            token: impersonatedToken,
            user: { ...impersonatedUser, impersonated: true },
          })
        );

        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Failed to impersonate user:", error);
    }
  };

  const handleCloseDSPModal = () => {
    closeModal("DSP_DELIVERY");
    setCurrentDSPPitchId(undefined);
    setCurrentDSPs([]);
  };

  const handleOpenSetReminder = (pitchId?: number) => {
    const id = pitchId ?? currentDSPPitchId;
    if (!id) return;
    const pitch = (filteredData as Pitch[]).find((p) => p.id === id);
    setReminderPitchId(id);
    setReminderArtistName(pitch?.artistName);
    setReminderReleaseTitle(pitch?.releaseTitle);
    if (isOpen("DSP_DELIVERY")) closeModal("DSP_DELIVERY");
    openModal("SET_REMINDER");
  };

  const handleCloseSetReminder = () => {
    closeModal("SET_REMINDER");
    setReminderPitchId(undefined);
    setReminderArtistName(undefined);
    setReminderReleaseTitle(undefined);
  };

  const handleDSPSubmit = async (selectedDSPs: string[]) => {
    if (!currentDSPPitchId) return;

    try {
      await pitchesApi.updateDelivery(currentDSPPitchId, {
        status: "delivered",
        deliveredDSPs: selectedDSPs,
      });
      handleCloseDSPModal();
      await fetchPitches(currentPage);
      Swal.fire({
        title: "Success!",
        text: "Pitch delivered successfully!",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error: any) {
      Swal.fire({
        title: "Error!",
        text: error?.response?.data?.message || "Failed to update delivery",
        icon: "error",
      });
    }
  };

  const handleOpenDeleteModal = (pitchId: number) => {
    setCurrentDeletePitchId(pitchId);
    setDeleteInput("");
    openModal("DELETE_CONFIRMATION");
  };

  const handleCloseDeleteModal = () => {
    closeModal("DELETE_CONFIRMATION");
    setCurrentDeletePitchId(undefined);
    setDeleteInput("");
  };

  const handleDeleteConfirmed = async () => {
    if (deleteInput !== "Delete" || !currentDeletePitchId) return;

    setModalLoading(true);
    try {
      await pitchesApi.deleteDraft(currentDeletePitchId);
      handleCloseDeleteModal();
      await fetchPitches(currentPage);
      Swal.fire({
        title: "Deleted!",
        text: "Your pitch has been deleted successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error: any) {
      Swal.fire({
        title: "Error!",
        text: error?.response?.data?.message || "Failed to delete pitch",
        icon: "error",
      });
    } finally {
      setModalLoading(false);
    }
  };

  // Bulk delete handler for selected pitches
  const handleBulkDeleteConfirmed = async () => {
    setModalLoading(true);
    try {
      const response = await pitchesApi.deletePitch(selectedPitchIds);
      setSelectedPitchIds([]);
      closeModal("BULK_DELETE");
      setDeleteInput("");
      await fetchPitches(currentPage);

      Swal.fire({
        title: response?.data?.success ? "Deleted!" : "Error!",
        text: response?.data?.message || "Pitch deleted successfully!",
        icon: response?.data?.success ? "success" : "error",
        confirmButtonText: "OK",
      });
    } catch (error: any) {
      closeModal("BULK_DELETE");
      setDeleteInput("");
      Swal.fire({
        title: "Error!",
        text: error?.response?.data?.message || error.message || "Request failed",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setModalLoading(false);
    }
  };

  // User App Updates handlers
  const handleDismissUpdate = async (updateId: number) => {
    // TODO: Implement dismiss logic via API
    console.log("Dismiss update:", updateId);
  };


  // Debounced search effect.
  //
  // This used to skip the fetch entirely while a previous one was in flight
  // (`if (!pitchesLoading)`). Because `pitchesLoading` was not a dependency,
  // nothing re-ran once that request finished, so the filter change was dropped
  // for good and the table kept showing the previous filter's rows while the
  // pills showed the new selection. Toggling slowly always worked; toggling
  // quickly desynced. The debounce is what protects against request storms, so
  // the fetch is now unconditional and staleness is handled in fetchPitches.
  useEffect(() => {
    // Enter the loading state immediately, before the 500ms debounce. Otherwise
    // there is a window where the filter/search has changed but the fetch has
    // not started yet: `pitchesLoading` is still false and `filteredData` still
    // holds the previous filter's (often empty) result, so the table briefly
    // renders the empty state — e.g. flashing "No pitches yet" when switching a
    // no-result filter back to All — before the skeleton and real data appear.
    setPitchesLoading(true);
    const timeoutId = setTimeout(() => {
      fetchPitches(1);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timeoutId);
    // pitchSearchType is intentionally NOT a dependency: changing the type clears
    // the term, and searching an empty term against a new field is a wasted call.
    // The next keystroke fetches with the new type (fetchPitches reads it directly).
  }, [pitchSearchTerm, pitchStatusFilter]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    const timeoutId = setTimeout(() => {
      fetchUsers(1);
      setUsersCurrentPage(1);
    }, 500);

    return () => clearTimeout(timeoutId);
    // userSearchType intentionally omitted — see the pitch search effect above.
  }, [userSearchTerm]);

  const fetchPitches = useCallback(async (
    pitchPage: number = currentPage
  ) => {
    // Only the newest request may write to state. Toggling filters quickly can
    // leave two requests in flight, and without this the slower (older) one
    // resolves last and overwrites the newer results — which is how the table
    // and the "Matching Pitches" count ended up disagreeing with the pills.
    const requestId = ++pitchRequestRef.current;
    const isCurrent = () => requestId === pitchRequestRef.current;

    try {
      setPitchesLoading(true);
      const pitchesResponse = await pitchesApi.getAll({
        page: pitchPage,
        limit: 10,
        search: pitchSearchTerm,
        searchType: pitchSearchType,
        status: pitchStatusFilter,
      });
      if (!isCurrent()) return;
      setData(pitchesResponse.data.data || []);
      setFilteredData(pitchesResponse.data.data || []);
      setTotalPages(pitchesResponse.data.pagination.totalPages);
      setTotalPitches(pitchesResponse.data.pagination.totalItems ?? 0);
    } catch (error) {
      if (!isCurrent()) return;
      setData([]);
      setFilteredData([]);
    } finally {
      // A superseded request must not clear the spinner: a newer one is still
      // running behind it.
      if (isCurrent()) setPitchesLoading(false);
    }
  }, [pitchSearchTerm, pitchSearchType, pitchStatusFilter]);

  const fetchUsers = useCallback(async (
    userPage: number = usersCurrentPage
  ) => {
    try {
      setUsersLoading(true);
      const usersResponse = await adminApi.getUsers({
        page: userPage,
        limit: 10,
        search: userSearchTerm,
        searchType: userSearchType,
      });
      setUsers(usersResponse.data.data || []);
      setFilteredUsers(usersResponse.data.data || []);
      setUsersTotalPages(usersResponse.data.pagination.totalPages);
      setTotalUsers(usersResponse.data.pagination.totalItems ?? 0);
    } catch (error) {
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [userSearchTerm, userSearchType]);

  const fetchData = useCallback(async (
    pitchPage: number = currentPage,
    userPage: number = usersCurrentPage
  ) => {
    if (user?.role === "admin") {
      await Promise.all([fetchPitches(pitchPage), fetchUsers(userPage)]);
    } else {
      await fetchPitches(pitchPage);
    }
  }, [user?.role, fetchPitches, fetchUsers]);



  const promote = async (userId: number) => {
    try {
      await adminApi.promoteUser(userId);
      Swal.fire({
        title: "Success!",
        text: "User Promoted Successfully!",
        icon: "success",
      });
      await fetchUsers(usersCurrentPage);
    } catch (error: any) {
      Swal.fire({
        title: "Error!",
        text: error.response?.data?.message || "Request failed",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const deleteUser = async (userId: number) => {
    try {
      const result = await confirmDanger({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        await adminApi.deleteUser(userId);
        Swal.fire({
          title: "Deleted!",
          text: "User Deleted Successfully!",
          icon: "success",
        });
        await fetchUsers(usersCurrentPage);
      }
    } catch (error: any) {
      Swal.fire({
        title: "Error!",
        text:
          error?.response?.data?.message || error.message || "Request failed",
        icon: "error",
        confirmButtonText: "OK",
      });
      console.error(error);
    }
  };


  const handlePitchPageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchPitches(newPage);
    }
  };

  const handleUserPageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= usersTotalPages) {
      setUsersCurrentPage(newPage);
      fetchUsers(newPage);
    }
  };

  const handlePitchStatusFilterChange = (status: string) => {
    setPitchStatusFilter(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  // const clearUserFilters = () => {
  //   setUserSearchTerm("");
  // };

  // Clears the status pills only. The search term is cleared by the × inside
  // the search input, so each control owns exactly one piece of filter state.
  const clearPitchStatusFilters = () => {
    setPitchStatusFilter([]);
  };


  const handleRowCheck = (id: number, checked: boolean) => {
    setSelectedPitchIds(prev =>
      checked ? [...prev, id] : prev.filter(pid => pid !== id)
    );
  };

  // Select all logic
  const allChecked = filteredData.length > 0 && filteredData.every((row: Pitch) => selectedPitchIds.includes(row.id));

  const handleCheckAll = (checked: boolean) => {
    const pageIds = filteredData.map((row: Pitch) => row.id);
    if (checked) {
      // Add current page IDs to selectedPitchIds (avoid duplicates)
      setSelectedPitchIds(prev =>
        Array.from(new Set([...prev, ...pageIds]))
      );
    } else {
      // Remove only current page IDs from selectedPitchIds
      setSelectedPitchIds(prev =>
        prev.filter(id => !pageIds.includes(id))
      );
    }
  };

  const userMenuItemClass = (active: boolean, danger = false) =>
    `${active ? "bg-primary-50" : ""} group flex w-full items-center px-4 py-2 text-sm transition-colors ${
      danger ? "text-danger-600" : "text-neutral-700"
    }`;

  // DashboardTableHeader renders the real <thead> (its column set depends on the
  // admin flag it reads from localStorage). This mirrors that set so DataTable's
  // loading and empty rows span the correct number of columns.
  const isAdminView = user?.role === "admin";

  // Display labels for the status pills. These mirror the short badge text in
  // DashboardTableRow.getStatusBadge so a pill reads the same as the badge it
  // filters for. The map key is the raw status sent to the API.
  const pitchStatusLabels: Record<string, string> = {
    draft: "Draft",
    "Needs Pitch": "Needs Pitch",
    submitted: "Submitted",
    delivered: "Delivered",
    "submitted - update pending": "Submitted Upd. Pend",
    "delivered - update pending": "Delivered Upd. Pend",
    "delivered - re-delivery needed": "Re-delivery",
  };

  // Status values sent to the API verbatim. "Needs Pitch" is capitalised
  // because that is how it is stored, unlike the lower-cased statuses.
  const pitchStatusOptions: string[] = [
    "draft",
    "Needs Pitch",
    "submitted",
    "delivered",
    ...(isAdminView
      ? [
          "submitted - update pending",
          "delivered - update pending",
          "delivered - re-delivery needed",
        ]
      : []),
  ];

  const pitchFiltersActive =
    pitchSearchTerm.length > 0 || pitchStatusFilter.length > 0;

  // Greeting name: profile first name, else the email local part, else the
  // role. Profile loads after first paint, so every branch has to read well
  // on its own - "Welcome back" alone is the floor, never "Welcome back, ".
  const greetingName = (() => {
    const first = userFullName?.trim().split(/\s+/)[0];
    if (first) return first;
    const local = user?.email?.split("@")[0];
    if (local) return local.charAt(0).toUpperCase() + local.slice(1);
    return isAdminView ? "Admin" : "";
  })();

  /*
   * Stat summary row. Every figure is a real value already in state:
   * totalPitches / totalUsers are the server's pagination.totalItems, and the
   * quota numbers come from the pitches permission or the purchase record.
   * No trend pills anywhere - nothing in this data supports a period
   * comparison, and inventing one would misrepresent it.
   */
  const statCards: {
    key: string;
    label: string;
    value: React.ReactNode;
    loading?: boolean;
    icon: React.ReactNode;
  }[] = [
    {
      key: "pitches",
      // totalPitches reflects the active search/status filter, so the label has
      // to say so rather than claiming to be an absolute total.
      label: pitchFiltersActive ? "Matching Pitches" : "Total Pitches",
      value: totalPitches,
      loading: pitchesLoading,
      icon: <DocumentTextIcon className="w-5 h-5" />,
    },
    ...(isAdminView
      ? [
          {
            key: "users",
            label: "Total Users",
            value: totalUsers,
            loading: usersLoading,
            icon: <UsersIcon className="w-5 h-5" />,
          },
        ]
      : [
          ...(userType === "subscription" && pitches?.used != null
            ? [
                {
                  key: "used",
                  label: "Pitches Used",
                  value: pitches.used,
                  icon: <CheckCircleIcon className="w-5 h-5" />,
                },
              ]
            : []),
          {
            key: "remaining",
            label: "Remaining Pitches",
            value:
              userType === "subscription"
                ? pitches?.limit === -1
                  ? "Unlimited"
                  : Math.max((pitches?.limit ?? 0) - (pitches?.used ?? 0), 0)
                : profileData.purchase?.pitchLimit ?? 0,
            icon: <LightBulbIcon className="w-5 h-5" />,
          },
        ]),
  ];
  const pitchColumns: DataTableColumn[] = [
    ...(isAdminView
      ? [{ key: "select", header: "" }, { key: "company", header: "Company" }]
      : []),
    { key: "artist", header: "Artist" },
    { key: "release", header: "Release" },
    { key: "date", header: "Date" },
    { key: "pitchDue", header: "Pitch Due" },
    ...(isAdminView ? [{ key: "submitted", header: "Date Submission" }] : []),
    { key: "status", header: "Pitch Status" },
    { key: "action", header: "Action" },
  ];

  const userColumns: DataTableColumn<any>[] = [
    { key: "id", header: "ID", cell: (u) => u?.id ?? "N/A" },
    {
      key: "fullName",
      header: "User Name",
      cell: (u) => (
        <div className="flex items-center gap-2.5">
          <Avatar name={u?.fullName} email={u?.email} src={u?.profilePicture} />
          <span className="font-medium">{u?.fullName ?? "N/A"}</span>
        </div>
      ),
    },
    { key: "email", header: "Email", cell: (u) => u?.email ?? "N/A" },
    { key: "company", header: "Company", cell: (u) => u?.Label?.name ?? "N/A" },
    {
      key: "role",
      header: "Role",
      className: "capitalize",
      cell: (u) => u?.role ?? "N/A",
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      cell: (u) => (
        <Menu as="div" className="relative inline-block text-left">
              <Menu.Button className={rowMenuTriggerClass}>
                <EllipsisVerticalIcon className="w-5 h-5" />
              </Menu.Button>
              <Menu.Items
                modal={false}
                transition
                anchor={{ to: "bottom end", gap: 4 }}
                className={cn(rowMenuPanelClass, rowMenuTransitionClass)}
              >
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => navigate(`/admin/userdsp/${u.id}`)}
                        className={userMenuItemClass(active)}
                      >
                        <EyeIcon className="flex-shrink-0 w-4 h-4 mr-2.5 text-neutral-500" />
                        View User
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => promote(u.id)}
                        className={userMenuItemClass(active)}
                      >
                        <ArrowUpIcon className="flex-shrink-0 w-4 h-4 mr-2.5 text-neutral-500" />
                        Promote User
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => deleteUser(u.id)}
                        className={userMenuItemClass(active, true)}
                      >
                        <TrashIcon className="flex-shrink-0 w-4 h-4 mr-2.5" />
                        Remove User
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => handleImpersonateUser(u.id)}
                        className={userMenuItemClass(active)}
                      >
                        <UserCircleIcon className="flex-shrink-0 w-4 h-4 mr-2.5 text-neutral-500" />
                        Impersonate User
                      </button>
                    )}
                  </Menu.Item>
              </Menu.Items>
        </Menu>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        breadcrumbs={[{ label: "Dashboard" }]}
        className="mb-6"
        title={greetingName ? `Welcome back, ${greetingName}` : "Welcome back"}
        actions={
          <>
          {user?.role === "admin" && (
            <Button onClick={() => navigate("/admin/invite")} className="w-full sm:w-auto">
              <UserPlusIcon className="w-4 h-4" /> Invite User
            </Button>
          )}
          {user?.role !== "admin" && (
            <>
              <Button
            variant="secondary"
            onClick={() => setShowCSVUpload(true)}
            className="w-full sm:w-auto"
          >
            Upload Release Schedule
          </Button>
          <Button
            id="create-pitch-btn"
            onClick={() => navigate("/pitch/create")}
            className="w-full sm:w-auto"
          >
            <LightBulbIcon className="w-4 h-4" /> Create Pitch
          </Button>
             </>
          )}
          </>
        }
      />

      <div
        className={`grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 ${
          statCards.length >= 3 ? "lg:grid-cols-3" : ""
        }`}
      >
        {statCards.map((card) => (
          <StatCard
            key={card.key}
            label={card.label}
            value={card.value}
            loading={card.loading}
            icon={card.icon}
          />
        ))}
      </div>

      {/* Section tabs - admin only, since basic users have no Users table. */}
      {isAdminView && (
        <SegmentedTabs
          className="mb-4"
          ariaLabel="Dashboard sections"
          value={activeSection}
          onChange={(key) => setActiveSection(key as "pitches" | "users")}
          items={[
            { key: "pitches", label: "Pitches" },
            { key: "users", label: "Users" },
          ]}
        />
      )}

      {(!isAdminView || activeSection === "pitches") && (
      <>
      {/* Pitches Section Header with Search */}
      <div className="flex flex-col gap-4 px-4 py-3 bg-white border rounded-t-lg border-neutral-200 sm:flex-row sm:items-center">
        <h3 className="text-base font-semibold text-black">Pitches</h3>
        {
          selectedPitchIds.length > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-neutral-600">
                {selectedPitchIds.length} selected
              </span>
              <Button
                variant="dangerOutline"
                onClick={() => {
                  setDeleteInput("");
                  openModal("BULK_DELETE");
                }}
                aria-label="Delete selected pitches"
              >
                <TrashIcon className="w-4 h-4" />
                <span>Delete Selected Pitches</span>
              </Button>
            </div>
          )
        }



        {/*
          Search type + term are one control: a single border, radius and
          focus-within ring wrap both, with an inset divider between them. The
          × clears the term; the status pills have their own clear control.
        */}
        <div className="flex items-stretch w-full bg-white border rounded-lg border-neutral-300 focus-within:border-primary-500 sm:w-auto sm:ml-auto">
          {/* Search Type Selector */}
          <div className="relative shrink-0">
            <select
              value={pitchSearchType}
              onChange={(e) => setPitchSearchType(e.target.value as "artist" | "release" | "company")}
              aria-label="Search pitches by field"
              // A <select> sizes to its widest option, which starved the input
              // on narrow screens - cap it below sm and let it size naturally above.
              className="w-[104px] sm:w-auto h-full py-2 pl-3 text-sm truncate bg-transparent border-0 appearance-none cursor-pointer pr-7 text-neutral-600 focus:outline-none focus:ring-0"
            >
              <option value="artist">Artist Name</option>
              <option value="release">Release Name</option>
              <option value="company">Company Name</option>
            </select>
            <ChevronDownIcon className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-2 top-1/2 text-neutral-400" />
          </div>

          <span aria-hidden="true" className="self-stretch w-px my-1.5 bg-neutral-200" />

          {/* Search Input. min-w-0 lets flex-1 actually shrink below content width. */}
          <div className="relative flex-1 min-w-0 sm:w-64">
            <MagnifyingGlassIcon className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-neutral-400" />
            <input
              type="text"
              value={pitchSearchTerm}
              onChange={(e) => setPitchSearchTerm(e.target.value)}
              placeholder={`Search by ${pitchSearchType === "artist" ? "artist name" : pitchSearchType === "release" ? "release name" : "company name"}...`}
              className="w-full py-2 text-sm text-black bg-transparent border-0 rounded-r-lg pl-9 pr-9 placeholder-neutral-400 focus:outline-none focus:ring-0"
            />
            {pitchSearchTerm && (
              <button
                onClick={() => setPitchSearchTerm("")}
                className="border border-transparent absolute p-0.5 -translate-y-1/2 rounded right-2 top-1/2 text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700"
                aria-label="Clear pitch search"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/*
        Status quick-filters. Multi-select, exactly as the old checkbox panel
        was: clicking a pill toggles that status in pitchStatusFilter. No count
        badges - the API returns a total only for the active filter, so a real
        per-status count would need a dedicated endpoint.
      */}
      {/*
        The funnel icon is decoration here, not a control, so it sits inside the
        scrolling row rather than stealing fixed width from the pills - on a
        phone every pixel of this row belongs to the pills.

        There is deliberately no separate "Clear" button: the leading "All" pill
        already clears the selection, and a second control doing the same job
        only competed for width and truncated the pill beside it.
      */}
      <div className="bg-white border-t border-x border-neutral-200 border-t-neutral-100">
        <FilterPills
          className="py-2.5"
          insetClassName="px-3"
          leading={
            <FunnelIcon
              aria-hidden="true"
              className="w-4 h-4 shrink-0 text-neutral-400"
            />
          }
          items={pitchStatusOptions.map((status) => ({
            key: status,
            label: pitchStatusLabels[status] ?? status,
          }))}
          active={pitchStatusFilter}
          onToggle={handlePitchStatusFilterChange}
          allLabel="All"
          onClearAll={clearPitchStatusFilters}
        />
      </div>

      {/* Pitches Table */}
      <DataTable
        className="rounded-t-none"
        data-tour="pitches-table"
        tableId="pitches-table"
        columns={pitchColumns}
        loading={pitchesLoading}
        head={
          <DashboardTableHeader
            allChecked={allChecked}
            onCheckAll={handleCheckAll}
          />
        }
      >
        {filteredData.length > 0 ? (
          filteredData.map((row: Pitch, index) => (
            <DashboardTableRow
              key={Number(row.id)}
              data={row}
              onStatusChange={() => fetchPitches(currentPage)}
              checked={selectedPitchIds.includes(row.id)}
              onCheck={handleRowCheck}
              onOpenDSPModal={handleOpenDSPModal}
              onOpenDeleteModal={handleOpenDeleteModal}
              onSetReminder={user?.role === "admin" ? handleOpenSetReminder : undefined}
              isFirstRow={index === 0}
            />
          ))
        ) : (
          <tr>
            <td className="px-4 py-14" colSpan={pitchColumns.length}>
              {/*
                Two different empty states: a filtered miss is recoverable by
                clearing filters, a genuinely empty table is not - offering
                "Clear filters" there would point at nothing.
              */}
              <div className="flex flex-col items-center justify-center text-center">
                <span className="inline-flex items-center justify-center w-12 h-12 mb-3 rounded-full bg-primary-50 text-primary-700">
                  {pitchFiltersActive ? (
                    <MagnifyingGlassIcon className="w-6 h-6" />
                  ) : (
                    <DocumentTextIcon className="w-6 h-6" />
                  )}
                </span>
                <p className="text-sm font-medium text-black">
                  {pitchFiltersActive ? "No matching pitches" : "No pitches yet"}
                </p>
                <p className="mt-1 text-sm text-neutral-500">
                  {pitchFiltersActive
                    ? "Try a different search term or status filter."
                    : isAdminView
                      ? "Pitches submitted by users will appear here."
                      : "Create your first pitch to see it here."}
                </p>
              </div>
            </td>
          </tr>
        )}
      </DataTable>

      <div className="mt-3">
        <Pagination
          page={currentPage}
          totalPages={totalPages}
          setPage={handlePitchPageChange}
          totalItems={totalPitches}
          limit={10}
        />
      </div>
      </>
      )}

      {isAdminView && activeSection === "users" && (
        <div>
          {/* Users Section Header with Search */}
          <div className="flex flex-col gap-4 px-4 py-3 bg-white border rounded-t-lg border-neutral-200 sm:flex-row sm:items-center">
            <h3 className="text-base font-semibold text-black">Users</h3>

            {/* Users Search - same unified control as the pitches search. */}
            <div className="flex items-stretch w-full bg-white border rounded-lg border-neutral-300 focus-within:border-primary-500 sm:w-auto sm:ml-auto">
              {/* Search Type Selector */}
              <div className="relative shrink-0">
                <select
                  value={userSearchType}
                  onChange={(e) => setUserSearchType(e.target.value as "name" | "email" | "company")}
                  aria-label="Search users by field"
                  className="w-[104px] sm:w-auto h-full py-2 pl-3 text-sm truncate bg-transparent border-0 appearance-none cursor-pointer pr-7 text-neutral-600 focus:outline-none focus:ring-0"
                >
                  <option value="name">User Name</option>
                  <option value="email">Email</option>
                  <option value="company">Company</option>
                </select>
                <ChevronDownIcon className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-2 top-1/2 text-neutral-400" />
              </div>

              <span aria-hidden="true" className="self-stretch w-px my-1.5 bg-neutral-200" />

              {/* Search Input */}
              <div className="relative flex-1 min-w-0 sm:w-64">
                <MagnifyingGlassIcon className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-neutral-400" />
                <input
                  type="text"
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  placeholder={`Search by ${userSearchType === "name" ? "user name" : userSearchType === "email" ? "email" : "company"}...`}
                  className="w-full py-2 text-sm text-black bg-transparent border-0 rounded-r-lg pl-9 pr-9 placeholder-neutral-400 focus:outline-none focus:ring-0"
                />
                {userSearchTerm && (
                  <button
                    onClick={() => setUserSearchTerm("")}
                    className="border border-transparent absolute p-0.5 -translate-y-1/2 rounded right-2 top-1/2 text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700"
                    aria-label="Clear user search"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>



          {/* Users Table */}
          <DataTable
            className="overflow-y-visible rounded-t-none"
            columns={userColumns}
            rows={filteredUsers}
            loading={usersLoading}
            getRowKey={(u: any, index: number) => u?.id ?? index}
            emptyMessage={
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <span className="inline-flex items-center justify-center w-12 h-12 mb-3 rounded-full bg-primary-50 text-primary-700">
                  <UsersIcon className="w-6 h-6" />
                </span>
                <p className="text-sm font-medium text-black">
                  {userSearchTerm ? "No matching users" : "No users yet"}
                </p>
                <p className="mt-1 text-sm text-neutral-500">
                  {userSearchTerm
                    ? "Try a different search term or field."
                    : "Invited users will appear here."}
                </p>
                {userSearchTerm && (
                  <Button
                    variant="secondary"
                    onClick={() => setUserSearchTerm("")}
                    className="mt-4"
                  >
                    <XMarkIcon className="w-4 h-4" />
                    Clear search
                  </Button>
                )}
              </div>
            }
          />

          <div className="mt-4">
            <Pagination
              page={usersCurrentPage}
              totalPages={usersTotalPages}
              setPage={handleUserPageChange}
              totalItems={totalUsers}
              limit={10}
            />
          </div>
        </div>
      )}
      {showCSVUpload && <div>
        <CSVUpload onClose={() => setShowCSVUpload(false)} onSuccess={() => fetchPitches(1)} userId={user?.id} />
      </div>}

      {isOpen("DSP_DELIVERY") && (
        <ModalRenderer
          type="DSP_DELIVERY"
          isOpen={isOpen("DSP_DELIVERY")}
          onClose={handleCloseDSPModal}
          pitchId={currentDSPPitchId}
          initialDSPs={currentDSPs}
          mode={currentDSPMode}
          onSubmit={handleDSPSubmit}
        />
      )}

      {isOpen("SET_REMINDER") && reminderPitchId && (
        <ModalRenderer
          type="SET_REMINDER"
          isOpen={isOpen("SET_REMINDER")}
          onClose={handleCloseSetReminder}
          pitchId={reminderPitchId}
          artistName={reminderArtistName}
          releaseTitle={reminderReleaseTitle}
          onSuccess={() => fetchPitches(currentPage)}
        />
      )}

      {isOpen("DELETE_CONFIRMATION") && (
        <ModalRenderer
          type="DELETE_CONFIRMATION"
          isOpen={isOpen("DELETE_CONFIRMATION")}
          onClose={handleCloseDeleteModal}
          inputValue={deleteInput}
          setInputValue={setDeleteInput}
          onConfirm={handleDeleteConfirmed}
          loading={modalLoading}
        />
      )}

      {/* Bulk Delete Modal */}
      {isOpen("BULK_DELETE") && (
        <ModalRenderer
          type="DELETE_CONFIRMATION"
          isOpen={isOpen("BULK_DELETE")}
          onClose={() => {
            closeModal("BULK_DELETE");
            setDeleteInput("");
          }}
          inputValue={deleteInput}
          setInputValue={setDeleteInput}
          onConfirm={handleBulkDeleteConfirmed}
          loading={modalLoading}
        />
      )}

      {/* User App Updates Modal */}
      {isOpen("USER_APP_UPDATES") && user?.id && user?.role && (
        <ModalRenderer
          type="USER_APP_UPDATES"
          isOpen={isOpen("USER_APP_UPDATES")}
          onClose={() => closeModal("USER_APP_UPDATES")}
          onDismiss={handleDismissUpdate}
          userId={user.id}
          userRole={user.role}
        />
      )}
    </>
  );
}
